import type { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import Pagination from 'shared/components/pagination'
import useUser from 'shared/hooks/use-user'
import { useMembers } from 'modules/community/api/get-members'
import { MemberShort } from 'modules/community/components/member/member-short/member-short'
import { SkeletonMemberShort } from 'modules/community/components/member/member-short/member-short-skeleton'
import { MembersEmptyList } from 'modules/community/components/member/members-empty-list'
import { hasPermission } from 'modules/community/utils/abac-roles-permissions'

interface MembersTabContentProps {
  communityPath: string
  pageIndex: number
  searchQuery: string
  setPageIndex: (page: number) => void
  setRemovableMemberId: (id: number | null) => void
  setBanMemberId: (member: { userId: number; displayName: string } | null) => void
}

export const MembersTabContent = ({
  communityPath,
  pageIndex,
  searchQuery,
  setPageIndex,
  setRemovableMemberId,
  setBanMemberId,
}: MembersTabContentProps): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()

  const { data, isPending } = useMembers({
    communityPath,
    page: pageIndex,
    searchBy: searchQuery,
  })

  if (isPending) {
    return (
      <div className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] justify-center gap-5 px-5 sm:px-0 lg:justify-start lg:gap-3">
        {[...Array.from({ length: 9 }).keys()].map(index => (
          <SkeletonMemberShort key={index} />
        ))}
      </div>
    )
  }

  if (!data) {
    return null
  }

  const { data: members, total } = data

  if (total === 0) {
    return (
      <MembersEmptyList
        title={t('global.list.empty_title')}
        description={t('members.empty.state.description')}
      />
    )
  }

  return (
    <>
      {members.length > 0 && (
        <div className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] justify-center gap-5 px-5 sm:px-0 lg:justify-start lg:gap-3">
          {members.map(member => {
            const actionMenuItems: { label: string; onClick: () => void }[] = []
            if (!member.pending && hasPermission(user, 'members', 'delete', { member })) {
              actionMenuItems.push({
                label: t('settings.form.delete.label'),
                onClick: () => {
                  setRemovableMemberId(member.id)
                },
              })
            }
            if (hasPermission(user, 'members', 'ban', { member })) {
              actionMenuItems.push({
                label: t('settings.form.ban.label'),
                onClick: () => {
                  setBanMemberId({
                    userId: member.userId,
                    displayName: member.displayName,
                  })
                },
              })
            }
            return <MemberShort key={member.id} member={member} actionMenuItems={actionMenuItems} />
          })}
        </div>
      )}

      <div className="flex justify-center rounded-lg bg-bg-primary p-3">
        <Pagination
          total={total}
          onChangePage={setPageIndex}
          currentPage={pageIndex}
          pageSize={9}
        />
      </div>
    </>
  )
}
